// LIVE REFRESH — the pure parts (Karan 07/10/2026: "drive and mail are
// refreshing real time"). Mail is pushed (functions/mail-pulse.js writes
// mailPulse/{uid}; the screen listens); Drive is read from its changes feed
// every 30 seconds while a Drive screen is open and visible. Tested without a
// browser in test/live-refresh.test.mjs.

/** Collapse a burst of calls into one, `ms` after the last. */
export function makeDebounce(fn, ms = 2000, { setT = setTimeout, clearT = clearTimeout } = {}) {
  let t = null;
  const d = (...args) => { if (t) clearT(t); t = setT(() => { t = null; fn(...args); }, ms); };
  d.cancel = () => { if (t) clearT(t); t = null; };
  return d;
}

/**
 * The safety net under Gmail push: if no pulse and no refresh for `quietMs`
 * while the tab is visible, refresh once. Never more often than `quietMs` —
 * each refresh spends Gmail units (a thread list ~10, changed threads ~30).
 */
export function shouldSafetyRefresh({ lastActivityAt = 0, now = Date.now(), visible = true } = {}, { quietMs = 5 * 60 * 1000 } = {}) {
  return !!visible && now - (Number(lastActivityAt) || 0) >= quietMs;
}

/** A pulse is news when its historyId is newer than the last one acted on. */
export function isNewPulse(lastHistoryId, pulse) {
  const h = pulse && pulse.historyId != null ? String(pulse.historyId) : '';
  if (!h) return false;
  if (!lastHistoryId) return true;
  try { return BigInt(h) > BigInt(lastHistoryId); } catch (e) { return h !== String(lastHistoryId); }
}

/**
 * Which on-screen folders a batch of Drive changes touches. `changes` are
 * changes.list entries ({ fileId, removed, file: { parents } }); `watched` is
 * the folder ids the screen shows. A change counts when the changed item IS a
 * watched folder or SITS IN one. A removed file has no parents left, so it
 * matches by id against `known` (fileId → parent ids the screen last saw).
 */
export function foldersTouched(changes = [], watched = [], known = {}) {
  const w = new Set(watched.filter(Boolean));
  const hit = new Set();
  for (const c of changes) {
    if (!c) continue;
    const id = c.fileId || (c.file && c.file.id);
    if (id && w.has(id)) hit.add(id);
    const parents = (c.file && c.file.parents) || (id && known[id]) || [];
    for (const p of parents) if (w.has(p)) hit.add(p);
  }
  return [...hit];
}

/** Human, for the table note: "Updated just now" within a minute, else nothing. */
export function updatedNote(at, now = Date.now()) {
  return at && now - at < 60000 ? 'Updated just now' : '';
}
